"use client";

import { Text, SectionCard, CardTitle, CardDescription, Badge, Section } from "@/src/components";

const voiceExamples = [
  {
    context: "Error message",
    dont: "Error 500: Internal Server Error occurred during transaction processing.",
    do: "Something went wrong. Please try again, or contact support if this continues.",
  },
  {
    context: "Success message",
    dont: "Transaction ID #4829472 has been successfully processed and recorded in the system.",
    do: "Payment complete! You'll receive a confirmation email shortly.",
  },
  {
    context: "Empty state",
    dont: "No data available. The requested resource returned an empty result set.",
    do: "No items yet. Create your first one to get started.",
  },
  {
    context: "Loading state",
    dont: "Please wait while the system processes your request...",
    do: "Loading your data...",
  },
  {
    context: "Confirmation",
    dont: "Are you certain you wish to proceed with this irreversible action?",
    do: "Delete this item? This can't be undone.",
  },
];

const toneGuidelines = [
  {
    title: "Be Clear",
    description: "Use simple, direct language. Avoid jargon and technical terms unless necessary.",
    examples: [
      { bad: "Utilize the interface", good: "Use the interface" },
      { bad: "Leverage this functionality", good: "Use this feature" },
    ],
  },
  {
    title: "Be Concise",
    description: "Get to the point. Remove unnecessary words.",
    examples: [
      { bad: "In order to save your work", good: "To save your work" },
      { bad: "Click on the button", good: "Click the button" },
    ],
  },
  {
    title: "Be Helpful",
    description: "Guide users toward success. Explain what went wrong and how to fix it.",
    examples: [
      { bad: "Invalid input", good: "Enter a valid email address" },
      { bad: "Operation failed", good: "Couldn't save. Check your connection." },
    ],
  },
  {
    title: "Be Human",
    description: "Write like a friendly expert, not a robot or a bureaucrat.",
    examples: [
      { bad: "Authentication credentials required", good: "Please sign in to continue" },
      { bad: "Insufficient permissions", good: "You don't have access to this page" },
    ],
  },
];

export default function BrandVoicePage() {
  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Voice & Tone
            </Text>
            <Badge variant="secondary">Brand</Badge>
          </div>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            How we communicate with users shapes their experience. Our voice is
            consistent; our tone adapts to the context.
          </Text>
        </div>
      </Section>

      {/* Voice Principles */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Our Voice</CardTitle>
            <CardDescription>The consistent personality behind our words</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex flex-col gap-2">
            <Text as="p" className="font-semibold">Professional</Text>
            <Text as="p" className="text-sm text-muted-foreground">
              We're experts who know our craft. We communicate with confidence
              and authority, without being condescending.
            </Text>
          </div>
          <div className="flex flex-col gap-2">
            <Text as="p" className="font-semibold">Friendly</Text>
            <Text as="p" className="text-sm text-muted-foreground">
              We're approachable and helpful. We use conversational language
              that puts users at ease.
            </Text>
          </div>
          <div className="flex flex-col gap-2">
            <Text as="p" className="font-semibold">Clear</Text>
            <Text as="p" className="text-sm text-muted-foreground">
              We value the user's time. We say what we mean in as few words
              as possible.
            </Text>
          </div>
        </div>
      </SectionCard>
      </Section>

      {/* Tone Guidelines */}
      <Section>
        <div className="space-y-6">
        <Text as="h2" className="text-h4 font-semibold">
          Tone Guidelines
        </Text>
        <div className="space-y-4">
          {toneGuidelines.map((guideline) => (
            <SectionCard
              key={guideline.title}
              header={
                <>
                  <CardTitle className="text-base">{guideline.title}</CardTitle>
                  <CardDescription>{guideline.description}</CardDescription>
                </>
              }
              contentClassName="p-6"
            >
              <div className="space-y-3">
                {guideline.examples.map((example, i) => (
                  <div key={i} className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex flex-col gap-1 p-3 bg-destructive/5 rounded-lg border border-destructive/20">
                      <Text as="p" className="text-xs text-destructive font-medium">Don't</Text>
                      <Text as="p" className="text-muted-foreground">{example.bad}</Text>
                    </div>
                    <div className="flex flex-col gap-1 p-3 bg-success-bg rounded-lg border border-success/20">
                      <Text as="p" className="text-xs text-success font-medium">Do</Text>
                      <Text as="p">{example.good}</Text>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          ))}
        </div>
        </div>
      </Section>

      {/* Context Examples */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Writing for Different Contexts</CardTitle>
            <CardDescription>Adapt your tone to the situation</CardDescription>
          </>
        }
        contentClassName="p-6"
      >
        <div className="space-y-6">
          {voiceExamples.map((example) => (
            <div key={example.context} className="space-y-3">
              <Text className="font-medium">{example.context}</Text>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex flex-col gap-2 p-4 bg-destructive/5 rounded-lg border border-destructive/20">
                  <Text as="p" className="text-xs text-destructive font-medium">Don't</Text>
                  <Text as="p" className="text-muted-foreground">{example.dont}</Text>
                </div>
                <div className="flex flex-col gap-2 p-4 bg-success-bg rounded-lg border border-success/20">
                  <Text as="p" className="text-xs text-success font-medium">Do</Text>
                  <Text as="p">{example.do}</Text>
                </div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
      </Section>

      {/* Punctuation & Formatting */}
      <Section>
        <SectionCard
        header={
          <>
            <CardTitle>Punctuation & Formatting</CardTitle>
            <CardDescription>Consistency in the details</CardDescription>
          </>
        }
        contentClassName="p-6 space-y-4"
      >
        <ul className="space-y-3 text-sm">
          <li className="flex gap-3">
            <div className="text-primary font-bold">•</div>
            <div>Use sentence case for headings: "Create new project" not "Create New Project"</div>
          </li>
          <li className="flex gap-3">
            <div className="text-primary font-bold">•</div>
            <div>End button labels with periods only if they're full sentences</div>
          </li>
          <li className="flex gap-3">
            <div className="text-primary font-bold">•</div>
            <div>Use contractions: "can't" instead of "cannot"</div>
          </li>
          <li className="flex gap-3">
            <div className="text-primary font-bold">•</div>
            <div>Avoid exclamation marks except in celebratory moments</div>
          </li>
          <li className="flex gap-3">
            <div className="text-primary font-bold">•</div>
            <div>Use "and" instead of "&" in body text</div>
          </li>
          <li className="flex gap-3">
            <div className="text-primary font-bold">•</div>
            <div>Numbers: spell out one through nine, use numerals for 10+</div>
          </li>
        </ul>
      </SectionCard>
      </Section>
    </>
  );
}
